import { CartAddEvent, CartUpdateEvent } from "@theme/events";

export const icon = (name, className = "") =>
  `<span class="elumen-icon ${className}" aria-hidden="true">${document.querySelector("template[data-elumen-icons]")?.content.querySelector(`[data-icon="${name}"]`)?.innerHTML || ""}</span>`;
export const root = () =>
  window.StorefrontLocale?.root || window.Shopify?.routes?.root || "/";
export const escapeHTML = (value) =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ],
  );
export async function json(url, options = {}, fetcher = window.fetch) {
  const response = await fetcher(url, {
    ...options,
    headers: { Accept: "application/json", ...options.headers },
    signal: options.signal || AbortSignal.timeout(20000),
  });
  if (!response.ok) throw new Error("Die Daten konnten nicht geladen werden.");
  return response.json();
}
export const getCart = (fetcher) =>
  json(`${root()}cart.js`, { cache: "no-store" }, fetcher);
export const getProduct = (handle) =>
  json(`${root()}products/${encodeURIComponent(handle)}.js`, {
    cache: "no-store",
  });
export function quantityValid(value, rule = {}) {
  const n = Number(value);
  const min = rule.min ?? 1;
  return (
    String(value).trim() !== "" &&
    Number.isSafeInteger(n) &&
    n >= min &&
    (rule.max == null || n <= rule.max) &&
    (n - min) % (rule.increment || 1) === 0
  );
}
export function careSuggestions(cart, config) {
  if (
    !cart.items.some(
      (item) =>
        item.quantity > 0 &&
        !item.item_components?.length &&
        (config.colorHandles.includes(item.handle) ||
          config.colorProductTypes?.includes(item.product_type)),
    )
  )
    return [];
  const contained = new Set(
    cart.items.filter((item) => item.quantity > 0).map((item) => item.handle),
  );
  return config.care.filter(
    (group) => !group.handles.some((handle) => contained.has(handle)),
  );
}
let lastSuccessfulAdd = 0;
const variantQuantity = (cart, id) =>
  cart.items
    .filter((item) => String(item.variant_id || item.id) === String(id))
    .reduce((sum, item) => sum + item.quantity, 0);

/** Called only after explicit purchase intent, with the displayed exact variant. */
export async function addConfirmed(
  id,
  quantity,
  onChecking = () => {},
  expectedCurrency,
) {
  const run =
    window.deinFriseurCartQueue?.run ||
    ((task) => task(window.fetch.bind(window)));
  return run(async (fetcher) => {
    const before = await getCart(fetcher); // A failed preflight sends no write.
    if (expectedCurrency && before.currency !== expectedCurrency) {
      return {
        confirmed: false,
        cart: before,
        message:
          "Die Währung wurde geändert. Bitte lade die Seite neu, um die aktuellen Preise zu prüfen.",
      };
    }
    const sections = [
      ...new Set(
        [
          ...document.querySelectorAll("cart-items-component[data-section-id]"),
        ].map((el) => el.dataset.sectionId),
      ),
    ].slice(0, 5);
    let response,
      data,
      uncertain = false;
    try {
      response = await fetcher(`${root()}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [{ id, quantity }],
          sections,
          sections_url: location.pathname,
        }),
        signal: AbortSignal.timeout(20000),
      });
      data = await response.json();
    } catch {
      uncertain = true;
    }
    onChecking();
    let cart;
    try {
      cart = await getCart(fetcher);
    } catch {
      return {
        confirmed: false,
        message:
          "Der Warenkorbstatus konnte nicht bestätigt werden. Bitte prüfe deinen Warenkorb.",
      };
    }
    const delta = variantQuantity(cart, id) - variantQuantity(before, id);
    const ok =
      !uncertain && response?.ok && !data?.status && delta === quantity;
    const eventData = {
      source: "elumen-purchase",
      itemCount: cart.item_count,
      sections: data?.sections || {},
      variantId: String(id),
    };
    // Theme components retain their own section rendering and drawer/focus behavior.
    if (ok) lastSuccessfulAdd = Date.now();
    document.dispatchEvent(
      ok
        ? new CartAddEvent(cart, "elumen-purchase", eventData)
        : new CartUpdateEvent(cart, "elumen-purchase", eventData),
    );
    document.dispatchEvent(
      new CustomEvent("cart:updated", { detail: { cart } }),
    );
    if (ok)
      document.dispatchEvent(
        new CustomEvent("elumen:added", { detail: { cart } }),
      );
    if (ok)
      return {
        confirmed: true,
        cart,
        message: `${quantity} Stück hinzugefügt.`,
      };
    if (
      !uncertain &&
      response?.status === 422 &&
      delta > 0 &&
      delta <= quantity
    ) {
      return {
        confirmed: false,
        cart,
        message: `Bestandsgrenze: Im Warenkorb sind ${delta} Stück mehr enthalten; angefragt waren ${quantity}. Bitte prüfe den Warenkorb, bevor du erneut hinzufügst.`,
      };
    }
    if (uncertain || delta !== 0)
      return {
        confirmed: false,
        cart,
        message:
          "Die Änderung konnte nicht eindeutig zugeordnet werden. Bitte prüfe den aktuellen Warenkorb, bevor du erneut hinzufügst.",
      };
    return {
      confirmed: false,
      cart,
      message:
        data?.description ||
        "Der Artikel wurde nicht hinzugefügt. Bitte prüfe Verfügbarkeit und Menge.",
    };
  });
}

const pendingVariants = new Set();
const uncertainVariants = new Set();
class ElumenPurchase extends HTMLElement {
  pending = false;
  async connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.innerHTML = '<p role="status">Produktdaten werden geladen …</p>';
    await this.load();
  }
  async load() {
    try {
      const [product, cart] = await Promise.all([
        getProduct(this.dataset.handle),
        getCart(),
      ]);
      if (!this.isConnected) return;
      this.product = product;
      this.currency = cart.currency;
      // An existing colour mapping can never silently fall back to another variant.
      if (
        this.dataset.variant &&
        !product.variants.some((v) => String(v.id) === this.dataset.variant)
      )
        throw new Error("Die Produktzuordnung konnte nicht bestätigt werden.");
      this.render();
    } catch (error) {
      this.innerHTML = `<p role="status">${escapeHTML(error.message)}</p><button type="button">Erneut versuchen</button>`;
      this.querySelector("button").onclick = () => this.load();
    }
  }
  render() {
    const p = this.product;
    const variants = this.dataset.variant
      ? p.variants.filter((v) => String(v.id) === this.dataset.variant)
      : p.variants;
    const options =
      variants.length > 1
        ? `<label>Variante <select name="variant" required><option value="">Bitte wählen</option>${variants.map((v) => `<option value="${v.id}">${escapeHTML(v.public_title || v.title)}${v.available ? "" : " – ausverkauft"}</option>`).join("")}</select></label>`
        : `<input type="hidden" name="variant" value="${variants[0]?.id || ""}"><p>${escapeHTML(variants[0]?.public_title || (variants[0]?.title === "Default Title" ? p.title.match(/\b\d+\s*ml\b/i)?.[0] || "" : variants[0]?.title))}</p>`;
    this.innerHTML = `<article class="elumen-buy">${p.featured_image ? `<div class="elumen-product-media"><img class="elumen-product-image" src="${escapeHTML(p.featured_image)}" alt="${escapeHTML(p.title)}" loading="lazy"></div>` : ""}<div class="elumen-product-info"><p class="elumen-brand">${escapeHTML(p.vendor === "Goldwell" && /Elumen/i.test(p.title) ? "Goldwell Elumen" : p.vendor || "Goldwell Elumen")}</p><h3>${escapeHTML(this.dataset.shade || (this.closest("elumen-care-recommendations") ? p.title.replace(/^Goldwell\s+/i, "").replace(/\s+\d+\s*ml\b/i, "") : p.title))}</h3>${this.dataset.shade ? '<p class="elumen-product-subtitle">Deine gewählte Nuance</p>' : ""}<form class="elumen-ajax-form">${options}<p data-price></p><div class="elumen-quantity"><button type="button" data-quantity="minus" aria-label="Menge verringern">${icon("minus")}</button><label><span class="visually-hidden">Menge</span><input name="quantity" type="number" min="1" step="1" value="1" required inputmode="numeric"></label><button type="button" data-quantity="plus" aria-label="Menge erhöhen">${icon("plus")}</button></div><button type="submit" disabled>${icon(this.closest("elumen-care-recommendations") ? "plus" : "cart")}<span>${this.closest("elumen-care-recommendations") ? "Hinzufügen" : "In den Warenkorb"}</span>${icon("caret", "ew-next")}</button></form><a class="elumen-details-link" data-product-link href="${root()}products/${encodeURIComponent(p.handle)}">${escapeHTML(({"RR@all": "RR@all Rot · 200 ml ansehen", "Bl@all": "Bl@all Blau · 200 ml ansehen", "NA@2": "NA@2 Naturasch · 200 ml ansehen", "VV@all": "VV@all Violett · 200 ml ansehen"})[this.dataset.shade] || "Produktdetails")}${icon("caret", "ew-next")}</a><p role="status" aria-live="polite" data-status></p><button type="button" data-check hidden>Warenkorb erneut prüfen</button></div></article>`;
    this.form = this.querySelector("form");
    this.querySelectorAll("[data-quantity]").forEach((control) =>
      control.addEventListener("click", () => {
        const input = this.form.elements.quantity;
        if (control.dataset.quantity === "plus") input.stepUp();
        else input.stepDown();
      }),
    );
    this.form.elements.variant.addEventListener("change", () =>
      this.updateVariant(),
    );
    this.form.addEventListener("submit", (event) => {
      event.preventDefault();
      this.buy();
    });
    this.querySelector("[data-check]").onclick = async () => {
      try {
        await getCart();
        uncertainVariants.delete(this.form.elements.variant.value);
        this.locked = false;
        this.querySelector("[data-status]").textContent =
          "Warenkorb geladen. Bitte prüfe die enthaltenen Mengen vor einem weiteren Kauf.";
        this.querySelector("[data-check]").hidden = true;
        this.updateVariant();
      } catch {
        this.querySelector("[data-status]").textContent =
          "Der Warenkorb ist weiterhin nicht erreichbar.";
      }
    };
    this.updateVariant();
    this.querySelector("[data-check]").hidden = !this.locked;
    if (this.locked)
      this.querySelector("[data-status]").textContent =
        "Bitte prüfe den Warenkorb vor einem weiteren Kauf dieser Variante.";
  }
  updateVariant() {
    const v = this.product.variants.find(
      (v) => String(v.id) === this.form.elements.variant.value,
    );
    this.locked = uncertainVariants.has(this.form.elements.variant.value);
    this.querySelector("[data-price]").textContent = v
      ? `${new Intl.NumberFormat(document.documentElement.lang || "de-DE", { style: "currency", currency: this.currency }).format(v.price / 100)}${v.available ? "" : " · Ausverkauft"}`
      : "Wähle zuerst eine Variante.";
    if (v?.unit_price_measurement && v.unit_price != null) {
      const unit = v.unit_price_measurement;
      this.querySelector("[data-price]").textContent +=
        ` (${new Intl.NumberFormat(document.documentElement.lang || "de-DE", { style: "currency", currency: this.currency }).format(v.unit_price / 100)} / ${unit.reference_value === 1 ? "" : unit.reference_value}${unit.reference_unit})`;
    }
    this.querySelector('button[type="submit"]').disabled =
      this.pending ||
      this.locked ||
      !v?.available ||
      this.product.requires_selling_plan;
    this.querySelector("[data-check]").hidden = !this.locked;
    this.querySelector("[data-product-link]").href =
      `${root()}products/${encodeURIComponent(this.product.handle)}${v ? `?variant=${v.id}` : ""}`;
    const rule = v?.quantity_rule || {};
    const input = this.form.elements.quantity;
    input.min = rule.min || 1;
    input.step = rule.increment || 1;
    if (rule.max != null) input.max = rule.max;
    else input.removeAttribute("max");
  }
  async buy() {
    if (this.pending || this.locked) return;
    const id = this.form.elements.variant.value;
    const v = this.product.variants.find((v) => String(v.id) === id);
    const amount = this.form.elements.quantity.value;
    const status = this.querySelector("[data-status]");
    if (pendingVariants.has(id)) {
      status.textContent =
        "Dieser Artikel wird bereits hinzugefügt. Bitte warte auf die Bestätigung.";
      return;
    }
    if (
      this.product.requires_selling_plan ||
      !v?.available ||
      !quantityValid(amount, v.quantity_rule)
    ) {
      status.textContent =
        "Bitte wähle eine bestellbare Variante und eine zulässige ganze Menge.";
      return;
    }
    this.pending = true;
    pendingVariants.add(id);
    this.setAttribute("aria-busy", "true");
    this.form.querySelectorAll("input, select, button").forEach((el) => {
      el.disabled = true;
    });
    status.textContent = "Verfügbarkeit und Preis werden geprüft …";
    try {
      const fresh = await getProduct(this.dataset.handle);
      const current = fresh.variants.find(
        (variant) => String(variant.id) === id,
      );
      this.product = fresh;
      if (
        fresh.requires_selling_plan ||
        !current?.available ||
        !quantityValid(amount, current.quantity_rule)
      )
        throw new Error(
          "Verfügbarkeit oder Mengenregel hat sich geändert. Bitte prüfe deine Auswahl.",
        );
      if (current.price !== v.price) {
        this.product = fresh;
        throw new Error(
          "Der Preis wurde aktualisiert. Bitte prüfe den neuen Preis und bestätige den Kauf erneut.",
        );
      }
      status.textContent = "Wird hinzugefügt …";
      const result = await addConfirmed(
        id,
        Number(amount),
        () => {
          status.textContent = "Wir prüfen, ob der Artikel hinzugefügt wurde …";
        },
        this.currency,
      );
      status.textContent = `${this.product.title}: ${result.message}`;
      // Uncertain/partial outcomes require an explicit reconciliation before another write.
      this.locked = !result.confirmed;
      if (this.locked) uncertainVariants.add(id);
      else uncertainVariants.delete(id);
      this.querySelector("[data-check]").hidden = !this.locked;
    } catch (error) {
      status.textContent =
        error.message || "Der Artikel konnte nicht hinzugefügt werden.";
    } finally {
      this.pending = false;
      pendingVariants.delete(id);
      this.removeAttribute("aria-busy");
      this.form
        .querySelectorAll("input, select, [data-quantity]")
        .forEach((el) => {
          el.disabled = false;
        });
      this.updateVariant();
    }
  }
}
if (!customElements.get("elumen-purchase"))
  customElements.define("elumen-purchase", ElumenPurchase);

const configCache = new Map();
class ElumenCareRecommendations extends HTMLElement {
  revision = 0;
  connectedCallback() {
    const url = this.dataset.config;
    if (!configCache.has(url))
      configCache.set(
        url,
        json(url).catch((error) => {
          configCache.delete(url);
          throw error;
        }),
      );
    this.configPromise = configCache.get(url);
    document.addEventListener("cart:update", this.sync);
    document.addEventListener("cart:mutation-settled", this.sync);
    window.addEventListener("pageshow", this.sync);
    document.addEventListener("visibilitychange", this.onVisible);
    document.addEventListener("dialog:open", this.onCartOpen, true);
    this.sync();
  }
  disconnectedCallback() {
    this.revision++;
    document.removeEventListener("cart:update", this.sync);
    document.removeEventListener("cart:mutation-settled", this.sync);
    window.removeEventListener("pageshow", this.sync);
    document.removeEventListener("visibilitychange", this.onVisible);
    document.removeEventListener("dialog:open", this.onCartOpen, true);
  }
  onCartOpen = (event) => {
    if (event.target.matches("cart-drawer-component")) this.sync();
  };
  onVisible = () => {
    if (!document.hidden) this.sync();
  };
  sync = async () => {
    const revision = ++this.revision;
    try {
      this.config = await this.configPromise;
      const cart = await (window.deinFriseurCartQueue?.run((fetcher) =>
        getCart(fetcher),
      ) || getCart());
      const wizard = this.closest("elumen-wizard");
      const selectedColors = wizard
        ? [...wizard.querySelectorAll(".ew-products elumen-purchase")].map(
            (card) => ({ handle: card.dataset.handle, quantity: 1 }),
          )
        : [];
      const groups = careSuggestions(
        { ...cart, items: [...cart.items, ...selectedColors] },
        this.config,
      );
      const products = await Promise.all(
        groups.map(async (group) => {
          // Ordered explicit group handles, including confirmed separate pack sizes.
          for (const handle of group.handles) {
            try {
              const p = await getProduct(handle);
              if (p.variants.some((v) => v.available))
                return {
                  handle,
                  purpose: group.purpose,
                  variants: p.variants.map((v) => [v.id, v.available, v.price]),
                };
            } catch {
              /* Optional product: skip, never block the cart. */
            }
          }
          return null;
        }),
      );
      if (revision !== this.revision || !this.isConnected) return;
      const shown = products.filter(Boolean).slice(0, 2);
      const target = this.querySelector("[data-care-content]");
      const signature = JSON.stringify(shown);
      if (signature === this.signature) return;
      this.signature = signature;
      const hadFocus = target.contains(document.activeElement);
      target.replaceChildren();
      this.hidden = !shown.length;
      if (hadFocus && !shown.length)
        this.closest("cart-items-component")
          ?.querySelector("a, button")
          ?.focus({ preventScroll: true });
      if (shown.length) {
        const heading = document.createElement("h3");
        heading.textContent = wizard
          ? "Passt zu deiner Farbroutine"
          : "Passt zu deiner Auswahl";
        heading.tabIndex = -1;
        target.append(heading);
        if (hadFocus) heading.focus({ preventScroll: true });
        const intro = document.createElement("p");
        intro.textContent = "Optional hinzufügen";
        target.append(intro);
        const grid = document.createElement("div");
        grid.className = "elumen-care-grid";
        target.append(grid);
        for (const item of shown) {
          const card = document.createElement("elumen-purchase");
          card.dataset.handle = item.handle;
          grid.append(card);
        }
      }
    } catch {
      if (revision === this.revision) {
        this.hidden = true;
        this.signature = undefined;
      }
    }
  };
}
if (!customElements.get("elumen-care-recommendations"))
  customElements.define(
    "elumen-care-recommendations",
    ElumenCareRecommendations,
  );

class ElumenCartFeedback extends HTMLElement {
  connectedCallback() {
    document.addEventListener("elumen:added", this.show);
    if (Date.now() - lastSuccessfulAdd < 15000) this.show();
  }
  disconnectedCallback() {
    document.removeEventListener("elumen:added", this.show);
  }
  show = () => {
    this.hidden = false;
  };
}
if (!customElements.get("elumen-cart-feedback"))
  customElements.define("elumen-cart-feedback", ElumenCartFeedback);
